<template>
  <div class="insights-wrapper">
    <div class="insights-grid">
      <div
        v-for="(insight, i) in insights"
        :key="i"
        class="insight-card"
        :style="{ borderLeftColor: insight.color }"
      >
        <div class="insight-title">{{ insight.title }}</div>
        <div class="insight-text">{{ insight.text }}</div>
      </div>
    </div>

    <div class="insights-summary">
      <h3>Sammanfattning</h3>
      <div class="summary-grid">
        <div class="summary-stat">
          <span class="summary-value">{{ totalFlows }}</span>
          <span class="summary-label">Totala flöden</span>
        </div>
        <div class="summary-stat">
          <span class="summary-value">{{ uniqueCities }}</span>
          <span class="summary-label">Städer</span>
        </div>
        <div class="summary-stat">
          <span class="summary-value">{{ uniqueRoutes }}</span>
          <span class="summary-label">Unika rutter</span>
        </div>
        <div class="summary-stat">
          <span class="summary-value">{{ longestRoute.km }} km</span>
          <span class="summary-label">Längsta rutten</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const COLORS = {
  green: '#3A6B44',
  teal: '#2A7B6F',
  rose: '#B84040',
  gold: '#9A7B2A',
  purple: '#6B4A8A',
  slate: '#3A5A72'
};

export default {
  props: {
    nodes: { type: Array, required: true },
    rawFlows: { type: Array, required: true }
  },
  computed: {
    totalFlows() {
      return this.rawFlows.reduce((s, f) => s + f.count, 0);
    },
    uniqueCities() {
      return this.nodes.length;
    },
    uniqueRoutes() {
      return this.rawFlows.length;
    },
    longestRoute() {
      const nodeMap = new Map(this.nodes.map(n => [n.id, n]));
      let maxDist = 0;
      let label = '';
      for (const f of this.rawFlows) {
        const a = nodeMap.get(f.from);
        const b = nodeMap.get(f.to);
        if (!a || !b) continue;
        const dist = this.haversine(a.lat, a.lng, b.lat, b.lng);
        if (dist > maxDist) {
          maxDist = dist;
          label = `${f.from} → ${f.to}`;
        }
      }
      return { km: Math.round(maxDist), label };
    },

    insights() {
      const items = [];

      // Strongest single flow
      const topFlow = [...this.rawFlows].sort((a, b) => b.count - a.count)[0];
      if (topFlow) {
        items.push({
          title: 'Starkaste flödet',
          text: `${topFlow.from} \u2192 ${topFlow.to} med ${topFlow.count} ärenden. Detta är den mest trafikerade rutten.`,
          color: COLORS.green
        });
      }

      // Biggest net outflow city
      const outflowCity = [...this.nodes]
        .filter(n => n.outgoing > 0)
        .sort((a, b) => {
          const netA = a.outgoing - a.incoming;
          const netB = b.outgoing - b.incoming;
          return netB - netA;
        })[0];
      if (outflowCity) {
        const net = outflowCity.outgoing - outflowCity.incoming;
        items.push({
          title: 'Störst nettoutflöde',
          text: `${outflowCity.label} tappar flest boende med netto ${
            net > 0 ? '-' : '+'
          }${Math.abs(net)} (${outflowCity.outgoing} ut, ${
            outflowCity.incoming
          } in).`,
          color: COLORS.rose
        });
      }

      // Biggest net inflow city
      const inflowCity = [...this.nodes]
        .filter(n => n.incoming > 0)
        .sort((a, b) => {
          const netA = a.incoming - a.outgoing;
          const netB = b.incoming - b.outgoing;
          return netB - netA;
        })[0];
      if (inflowCity) {
        const net = inflowCity.incoming - inflowCity.outgoing;
        items.push({
          title: 'Störst nettoinflöde',
          text: `${inflowCity.label} attraherar flest med netto +${net} (${inflowCity.incoming} in, ${inflowCity.outgoing} ut).`,
          color: COLORS.teal
        });
      }

      // Most internal swaps
      const internalCity = [...this.nodes]
        .filter(n => (n.internal || 0) > 0)
        .sort((a, b) => (b.internal || 0) - (a.internal || 0))[0];
      if (internalCity) {
        items.push({
          title: 'Mest interna byten',
          text: `${internalCity.label} har ${internalCity.internal} byten inom samma ort. Bytarna byter bostad men stannar kvar.`,
          color: COLORS.gold
        });
      }

      // Hub city (most unique connections)
      const connections = {};
      this.rawFlows.forEach(f => {
        connections[f.from] = (connections[f.from] || new Set()).add(f.to);
        connections[f.to] = (connections[f.to] || new Set()).add(f.from);
      });
      const hubEntry = Object.entries(connections).sort(
        (a, b) => b[1].size - a[1].size
      )[0];
      if (hubEntry) {
        items.push({
          title: 'Mest uppkopplad ort',
          text: `${hubEntry[0]} har flöden till/från ${hubEntry[1].size} andra orter, vilket gör den till ett nav i bytesnätverket.`,
          color: COLORS.purple
        });
      }

      // One-directional flows
      const oneWay = this.rawFlows.filter(f => {
        const reverse = this.rawFlows.find(
          r => r.from === f.to && r.to === f.from
        );
        return !reverse;
      });
      if (oneWay.length > 0) {
        const topOneWay = [...oneWay].sort((a, b) => b.count - a.count)[0];
        items.push({
          title: 'Envägsflöden',
          text: `${oneWay.length} av ${this.rawFlows.length} rutter går bara i en riktning. Starkast: ${topOneWay.from} \u2192 ${topOneWay.to} (${topOneWay.count} ärenden) utan motflöde.`,
          color: COLORS.slate
        });
      }

      return items;
    }
  },
  methods: {
    haversine(lat1, lon1, lat2, lon2) {
      const R = 6371;
      const toRad = v => (v * Math.PI) / 180;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }
  }
};
</script>

<style scoped>
.insights-wrapper {
  padding: 24px 28px;
}

.insights-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

.insight-card {
  background: #fdfbf7;
  border-radius: 12px;
  border: 1px solid #d6cebf;
  border-left: 4px solid;
  padding: 20px 22px;
}

.insight-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1209;
  margin-bottom: 8px;
}

.insight-text {
  font-size: 1.2rem;
  color: #7a6e5f;
  line-height: 1.6;
}

.insights-summary {
  background: #fdfbf7;
  border-radius: 12px;
  border: 1px solid #d6cebf;
  padding: 24px 28px;
}

.insights-summary h3 {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1209;
  margin: 0 0 16px 0;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.summary-stat {
  text-align: center;
}

.summary-value {
  display: block;
  font-size: 2.4rem;
  font-weight: 800;
  color: #1a1209;
}

.summary-label {
  font-size: 1rem;
  color: #7a6e5f;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
}
</style>
